import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AlignmentPeriodCalendar } from '@vendasta/multi-location-analytics';
import dayjs from 'dayjs';
import {
  LocaleDateTickFormatting,
  MaxRank,
  SEODataPoint,
  SEOTableData,
  WholeNumberTickFormatting,
} from '../../local-seo';
import { chartBlue, chartGreen } from './local-seo-graph.config';

interface ChartConfig {
  seriesData: MultiSeriesValue<number>[];
  seriesTotal?: number;
  alignment?: AlignmentPeriodCalendar;
  colorOverrides?: SeriesValue<string>[];
  showXAxis?: boolean;
  showYAxis?: boolean;
  showLegend?: boolean;
  xAxisLabel?: string;
  yAxisLabel?: string;
  disableTooltip?: boolean;
  yAxisMin?: number;
  yAxisMax?: number;
  view?: number[];
  xAxisTickFormatting?: (val: any) => string;
  yAxisTickFormatting?: (val: any) => string;
}

interface SeriesValue<T> {
  name: any;
  value: T;
}

interface MultiSeriesValue<T> {
  name: string;
  series: SeriesValue<T>[];
}

@Component({
  selector: 'local-seo-graph',
  templateUrl: 'local-seo-graph.component.html',
  styleUrls: ['local-seo-graph.component.scss'],
  standalone: false,
})
export class LocalSEOGraphComponent implements OnInit, OnChanges {
  @Input() localSEOData: SEOTableData;
  config: ChartConfig;

  forceHoverState = true;
  legendConfig = [
    {
      legendTranslationKey: 'LOCAL_SEO.LOCAL_SEO.GRAPH.LEGEND.LOCAL_RANK',
      icon: 'stop',
      iconColor: chartGreen,
    },
    {
      legendTranslationKey: 'LOCAL_SEO.LOCAL_SEO.GRAPH.LEGEND.ORGANIC_RANK',
      icon: 'stop',
      iconColor: chartBlue,
    },
  ];

  constructor(private translate: TranslateService) {}

  ngOnChanges(): void {
    window.requestAnimationFrame(() => {
      this.forceHoverState = false;
    });

    // Dispatch a resize event on the next frame
    window.requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
      this.forceHoverState = true;
    });
  }

  ngOnInit() {
    this.config = this.buildChartConfig(this.localSEOData);
  }

  buildChartConfig(seoData: SEOTableData) {
    const defaultMultiConfig = <ChartConfig>{
      showXAxis: true,
      showYAxis: true,
      xAxisTickFormatting: LocaleDateTickFormatting,
      yAxisTickFormatting: WholeNumberTickFormatting,
      yAxisLabel: this.translate.instant('LOCAL_SEO.LOCAL_SEO.GRAPH.LABEL.YAXIS'),
      showLegend: false,
    };

    const range = seoData.metrics?.dateRange;

    const seriesData: MultiSeriesValue<number>[] = [
      {
        name: 'Google Maps (Local)',
        series: formatSeriesData(seoData.metrics?.localRank || []),
      },
      {
        name: 'Google Search (Organic)',
        series: formatSeriesData(seoData.metrics?.organicRank || []),
      },
    ];

    const alignment = getAlignmentPeriodForDayJSRange(dayjs(range.start), dayjs(range.end));

    return <ChartConfig>{
      ...defaultMultiConfig,
      seriesData: seriesData,
      colorOverrides: [
        { name: 'Google Maps (Local)', value: chartGreen },
        { name: 'Google Search (Organic)', value: chartBlue },
      ],
      alignment: alignment,
      yAxisTickFormatting(value) {
        value = Math.abs(value);
        if (value > MaxRank) {
          return '${MaxRank}+';
        }
        return value;
      },
      yAxisMax: -1,
    };
  }

  formatDataPointDate(date: Date, aligment: AlignmentPeriodCalendar) {
    if (aligment === AlignmentPeriodCalendar.CALENDAR_MONTH) {
      return dayjs.utc(date).format('MMM, YYYY');
    } else if (aligment === AlignmentPeriodCalendar.CALENDAR_WEEK) {
      return this.translate.instant('COMMON.WEEK_OF') + ' ' + dayjs.utc(date).format('MMM D, YYYY');
    }
    return dayjs.utc(date).format('MMM D, YYYY');
  }
}

function getAlignmentPeriodForDayJSRange(start: dayjs.Dayjs, end: dayjs.Dayjs): AlignmentPeriodCalendar {
  const numMonths = end.diff(start, 'months');
  if (numMonths >= 8) {
    return AlignmentPeriodCalendar.CALENDAR_MONTH;
  } else if (numMonths >= 2) {
    return AlignmentPeriodCalendar.CALENDAR_WEEK;
  }
  return AlignmentPeriodCalendar.CALENDAR_DAY;
}

function formatSeriesData(data: SEODataPoint[]) {
  return data
    .filter((e) => e.value > 0 && e.value <= MaxRank)
    .map((l) => {
      return {
        name: offsetToUTCZero(l.date.toString()),
        value: l.value * -1, // add the lowest rank as possible since 0 is no rank
      };
    });
}

export function offsetToUTCZero(date: string): Date {
  const offset = dayjs(date).utcOffset();
  return dayjs.utc(date).subtract(offset, 'm').toDate();
}
